import { nanoid } from '@reduxjs/toolkit'
import { TokenList } from '@uniswap/token-lists'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../state'
import { fetchTokenList } from '../state/lists/actions'

function useFetchListCallback(): (listUrl: string, sendDispatch?: boolean) => Promise<TokenList> {
  const dispatch = useDispatch<AppDispatch>()

  // note: prevent dispatch if using for list search or unsupported list
  return useCallback(
    async (listUrl: string, sendDispatch = true) => {
      const requestId = nanoid()
      if (sendDispatch) {
        dispatch(fetchTokenList.pending({ requestId, url: listUrl }))
      }
      // lazy load avj and token list schema
      const getTokenList = (await import('../utils/getTokenList')).default
      console.log(`[Fetch Callback] 📞 Starting fetch callback | Request ID: ${requestId} | URL: ${listUrl}`)
      const callbackStartTime = Date.now()
      
      return getTokenList(listUrl)
        .then((tokenList) => {
          const duration = Date.now() - callbackStartTime
          console.log(`[Fetch Callback] ✅ Success | Request ID: ${requestId} | Duration: ${duration}ms | Tokens: ${tokenList.tokens?.length || 0}`)
          
          if (sendDispatch) {
            dispatch(fetchTokenList.fulfilled({ url: listUrl, tokenList, requestId }))
          }
          return tokenList
        })
        .catch((error) => {
          const duration = Date.now() - callbackStartTime
          const errorCode = (error as any)?.code || 'UNKNOWN'
          const errorMessage = error instanceof Error ? error.message : String(error)
          
          // Always log errors with full context for debugging
          console.error(`[Fetch Callback] ❌ Error | Request ID: ${requestId} | Duration: ${duration}ms`)
          console.error(`[Fetch Callback] ❌ Error Code: ${errorCode} | Message: ${errorMessage}`)
          console.error(`[Fetch Callback] ❌ URL: ${listUrl}`)
          
          if (error instanceof Error && error.stack) {
            console.error(`[Fetch Callback] ❌ Stack:\n${error.stack}`)
          }
          
          // Special handling for connection errors
          const isConnectionError = error instanceof Error && (
            error.message.includes('ECONNRESET') ||
            error.message.includes('aborted') ||
            error.message.includes('EPIPE') ||
            error.name === 'AbortError'
          )
          
          if (isConnectionError) {
            console.error(`[Fetch Callback] ⚠️ CONNECTION ERROR DETECTED! Request ID: ${requestId}`)
            console.error(`[Fetch Callback] ⚠️ This will be handled gracefully - app will continue with existing tokens`)
          }
          
          if (sendDispatch) {
            // Always dispatch rejection to update state, but don't crash
            dispatch(fetchTokenList.rejected({ url: listUrl, requestId, errorMessage }))
          }
          
          // Don't throw - let the app continue with existing token list
          // Return empty list instead to prevent crashes
          return {
            name: 'Empty Token List',
            timestamp: new Date().toISOString(),
            version: { major: 1, minor: 0, patch: 0 },
            tokens: [],
            tags: {},
            keywords: [],
          } as TokenList
        })
    },
    [dispatch],
  )
}

export default useFetchListCallback
